import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface HeroSlide {
  id: string;
  title: string;
  content: string | null;
  media_url: string | null;
  media_type: string | null;
}

const FALLBACK_IMAGE_WEBP = "/lovable-uploads/hero-fallback.webp";
const FALLBACK_IMAGE = "/lovable-uploads/bdaf78b7-eb1d-474d-a183-fc4e5a25e4b5.png";
const SLIDE_INTERVAL = 5000;

const HeroSection = () => {
  const { t } = useLanguage();
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});

  useEffect(() => {
    const fetchHeroSlides = async () => {
      const { data } = await supabase
        .from("posts")
        .select("id, title, content, media_url, media_type")
        .eq("is_published", true)
        .eq("is_hero", true)
        .order("created_at", { ascending: false })
        .limit(10);

      if (data && data.length > 0) {
        setSlides(data);
      }
    };
    fetchHeroSlides();
  }, []);

  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (slides.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setIsTransitioning(true);
      setCurrentIndex((prev) => (prev + 1) % slides.length);
      setTimeout(() => setIsTransitioning(false), 700);
    }, SLIDE_INTERVAL);
  }, [slides.length]);

  useEffect(() => {
    startAutoSlide();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startAutoSlide]);

  const goToSlide = (index: number) => {
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 700);
    startAutoSlide();
  };

  const prevSlide = () => goToSlide((currentIndex - 1 + slides.length) % slides.length);
  const nextSlide = () => goToSlide((currentIndex + 1) % slides.length);

  const hasSlides = slides.length > 0;
  const currentSlide = hasSlides ? slides[currentIndex] : null;

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background - Dynamic Slides or Fallback */}
      <div className="absolute inset-0 z-0">
        {hasSlides ? (
          slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                index === currentIndex ? "opacity-100" : "opacity-0"
              }`}
            >
              {slide.media_type === "video" && slide.media_url ? (
                <video
                  ref={(el) => (videoRefs.current[slide.id] = el)}
                  src={slide.media_url}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              ) : slide.media_url ? (
                <img
                  src={slide.media_url}
                  alt={slide.title}
                  className="w-full h-full object-cover"
                  fetchPriority={index === 0 ? "high" : "auto"}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/30 to-background" />
              )}
              {/* Dark overlay for text readability */}
              <div className="absolute inset-0 bg-black/40" />
            </div>
          ))
        ) : (
          <>
            <picture>
              <source srcSet={FALLBACK_IMAGE_WEBP} type="image/webp" />
              <img
                alt="Intense workout session"
                className="w-full h-full object-scale-down"
                src={FALLBACK_IMAGE}
                width={1920}
                height={1280}
                fetchPriority="high"
              />
            </picture>
          </>
        )}
      </div>

      {/* Slide Navigation Arrows */}
      {hasSlides && slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 z-20 w-10 h-10 rounded-full bg-background/20 backdrop-blur-sm border border-border/30 flex items-center justify-center hover:bg-background/40 transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 z-20 w-10 h-10 rounded-full bg-background/20 backdrop-blur-sm border border-border/30 flex items-center justify-center hover:bg-background/40 transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        </>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl">
          <p
            className="text-primary font-semibold tracking-[0.3em] uppercase mb-4 opacity-0 animate-fade-in"
            style={{ animationDelay: "0.1s" }}
          >
            {t.hero.tagline}
          </p>
          <h1
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[0.9] mb-6 opacity-0 animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            {t.hero.title1}
            <br />
            <span className="text-primary text-glow">{t.hero.title2}</span>
          </h1>
          <p
            className="text-white/80 text-lg md:text-xl max-w-xl mb-8 opacity-0 animate-fade-in"
            style={{ animationDelay: "0.3s" }}
          >
            {t.hero.description}
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in"
            style={{ animationDelay: "0.4s" }}
          >
            <Link to="/login">
              <Button variant="hero" size="xl">
                {t.hero.startTrial}
                <ArrowRight className="ml-2" />
              </Button>
            </Link>
            <Button variant="heroOutline" size="xl">
              <Play className="mr-2" />
              {t.hero.watchVideo}
            </Button>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      {hasSlides && slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? "w-8 bg-primary"
                  : "w-2 bg-white/50 hover:bg-white/70"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HeroSection;
